import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { DesignContextProvider } from "Components/DesignContext";
import { Button } from "Components/ReactstrapSlim";
import { SearchAddressAutocomplete } from "./components";
import {
   useCtaActions,
   useCtaError,
   useCtaParam,
   usePlacesAddress,
} from "./hooks";

import classes from "./HeaderCta.module.scss";
import { SearchAddressAutocompleteRef } from "./components/SearchAddressAutocomplete/SearchAddressAutocomplete";
import { IPlacePredictionViewModel } from "Services/API/API";

enum ActionMode {
   WaitList = "wait-list",
   SpaceList = "space-list",
}

interface HeaderCtaProps {
   trackVPV?: () => void;
   variant?: "opacity" | "opacity-on-dark";
   placeholder?: string;
   listSpaceText?: string;
   joinWaitlistText?: string;
   buttonVariant?: string;
}

const HeaderCta: React.FC<HeaderCtaProps> = ({
   trackVPV,
   variant,
   placeholder,
   listSpaceText = "List your space",
   joinWaitlistText = "Join waitlist",
   buttonVariant = "primary",
}) => {
   const [inputValue, setInputValue] = useState("");
   const [submitOnPlaceLoad, setSubmitOnPlaceLoad] = useState(false);
   const { isLoading, handleSelect, selection: placeSelection } =
      usePlacesAddress();

   const { isLocationSelected, emptyValue, validLocation: isLocationValid, supportedCountry } =
      useCtaParam({ placeSelection, inputValue });

   const { errorMessage, hintMessage, checkValidation } = useCtaError({
      isLocationValid,
      isLocationSelected,
      isValueEmpty: emptyValue,
      placeSelection,
      isCountrySupported: supportedCountry,
   });

   const { spaceListHandler, waitListHandler } = useCtaActions({
      trackVPV,
      placeSelection,
      checkValidation,
   });

   const actionMode = useMemo(
      () =>
         supportedCountry || !placeSelection?.details?.addressParts
            ? ActionMode.SpaceList
            : ActionMode.WaitList,
      [supportedCountry, placeSelection]
   );

   const autocompleteRef = useRef<SearchAddressAutocompleteRef>(null);

   const handleSubmit = () => {
      //Try auto-select first option if it matches input value
      if(!location) {
         if(autocompleteRef.current?.options?.length) {
            const option = autocompleteRef.current.options[0];
            if(option.text == inputValue) {
               setSubmitOnPlaceLoad(true);
               handleSelect(option);               
               return;
            }
         }
      }
      
      spaceListHandler();      
   }

   const handleChange = useCallback(
      (places: IPlacePredictionViewModel[]) => {
         const place = places?.[0];
         handleSelect(place);
         setInputValue(place?.text ?? "");
      },
      [handleSelect, checkValidation]
   );

   useEffect(() => {
      if(submitOnPlaceLoad && !isLoading && location) {
         setSubmitOnPlaceLoad(false);
         spaceListHandler();
      }
   }, [submitOnPlaceLoad, isLoading, location, spaceListHandler]);

   return (
      <DesignContextProvider name="marketing" className="supplylp-hero-cta">
         <div
            className={classNames({
               [classes.opacity]: variant === "opacity",
            })}
         >
            <SearchAddressAutocomplete
               ref={autocompleteRef}
               className={classNames(classes.supplylpHeroCtaInput, {
                  [classes.listYourSpace]: actionMode === ActionMode.SpaceList,
                  [classes.waitList]: actionMode === ActionMode.WaitList,
               })}
               placeholder={placeholder}
               onChange={handleChange}
               onInputChange={setInputValue}
            />
            {!isLoading && errorMessage && (
               <div className={classes.errorMessage}>{errorMessage}</div>
            )}
            {hintMessage && (
               <div className={classes.hintMessage}>{hintMessage}</div>
            )}
         </div>
         {actionMode === ActionMode.SpaceList && (
            <Button
               color={buttonVariant}
               className="supplylp-hero-cta-button"
               disabled={isLoading}
               onClick={handleSubmit}
            >
               {listSpaceText}
            </Button>
         )}
         {actionMode === ActionMode.WaitList && (
            <>
               <Button
                  color={buttonVariant}
                  className="supplylp-hero-cta-button"
                  disabled={isLoading}
                  onClick={waitListHandler}
               >
                  {joinWaitlistText}
               </Button>
            </>
         )}
      </DesignContextProvider>
   );
};

export default HeaderCta;
